import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useStore } from "zustand";
import { store } from "@app/store";
import { useIsAdmin } from "./useRoles";
import {
  filterActiveVariations,
  getStepEnterAction,
  getStructureVariationKeys,
  getVariationReferenceKeys,
  isCampaignVisualsStep,
  isImagesStep,
  resolveSteps,
  VARIATION_PROGRESS_STAGE,
} from "@app/views/projects/wizardSteps";
import {
  resolveFallbackAsset,
  resolveVariation,
} from "./imageVariations/resolve";

export const DEMO_DELAY = 5000;

export const isDemoProject = (enabled, project) =>
  !!enabled && project?.status === "completed";

export const useWizardDemoEnabled = () => {
  const isAdmin = useIsAdmin({ ignoreOverride: true });
  const enabled = useStore(store, (state) => state.demoMode);
  return isAdmin && !!enabled;
};

export const getDemoVariations = (step, template, project) => {
  const campaign = project.campaigns?.find(
    (item) => item.id === project.selectedCampaignIds?.[0],
  );
  const linked = new Set(getStructureVariationKeys(step?.campaignStructure));
  return filterActiveVariations(
    template.imageVariations,
    isImagesStep(step) ? step.stages : undefined,
    campaign,
  )
    .filter((variation) => isImagesStep(step) || linked.has(variation.key))
    .map((variation) => resolveVariation(template, project, variation.key));
};

export const getDemoValues = (project) => ({
  templateId: project.templateId,
  sfType: project.drNumber
    ? "drNumber"
    : project.accountName
      ? "accountName"
      : "none",
  accountName: project.accountName,
  drNumber: project.drNumber,
  customerName: project.customer?.name ?? project.customer?.["Company-name"],
  product: project.product,
  hint: project.hint,
  personaId: project.personaId,
  verticalKey: project.verticalKey,
  musicPath: project.musicPath,
  voiceoverMode: project.voiceoverMode ?? "original",
  include3DDigitalTwin:
    project.include3DDigitalTwin ??
    project.campaigns?.find(
      (campaign) => campaign.id === project.selectedCampaignIds?.[0],
    )?.["Include-3D-Digital-Twin"],
});

const hasValue = (value) => value != null && value !== "";

// This controller deliberately has no mutation hooks or canonical-cache writes.
export const useVideoWizardDemo = (project, template) => {
  const [saved] = useState(() => structuredClone(project));
  const activeTemplateId = template.id ?? saved.templateId;
  const steps = useMemo(
    () => resolveSteps(activeTemplateId, template),
    [activeTemplateId, template],
  );
  const [navigation, setNavigation] = useState({ index: 0, visit: 0 });
  const [furthestStep, setFurthestStep] = useState(0);
  const step = steps[navigation.index];
  const [finishedVisit, setFinishedVisit] = useState(-1);
  const [revealedValues, setRevealedValues] = useState(() => new Set());
  const [revealedAssets, setRevealedAssets] = useState(() => new Set());
  const [uploadingKeys, setUploadingKeys] = useState(() => new Set());
  const [progressByKey, setProgressByKey] = useState({});
  const timers = useRef(new Set());
  const values = useMemo(() => getDemoValues(saved), [saved]);
  const variations = useMemo(
    () => getDemoVariations(step, template, saved),
    [step, template, saved],
  );
  const enterAction = getStepEnterAction(step, activeTemplateId);
  const hasLoader = typeof enterAction?.isPending === "function";
  const isStepPending = hasLoader && finishedVisit !== navigation.visit;

  const cancelTimers = useCallback(() => {
    timers.current.forEach(clearTimeout);
    timers.current.clear();
  }, []);

  const schedule = useCallback((callback, delay) => {
    const timer = setTimeout(() => {
      timers.current.delete(timer);
      callback();
    }, delay);
    timers.current.add(timer);
  }, []);

  const unavailable = useCallback((name) => {
    store.getState().addToast(`No saved result for "${name}" in this demo.`, {
      variant: "negative",
    });
  }, []);

  const savedAsset = useCallback(
    (name) => saved.assets?.find((asset) => asset.name === name),
    [saved],
  );

  const hasSavedVariation = useCallback(
    (variation) => {
      const asset = savedAsset(variation.key);
      const resolved = resolveFallbackAsset(saved, variation, asset) ?? asset;
      return !!(resolved?.filePath || resolved?.src);
    },
    [saved, savedAsset],
  );

  const simulateVariations = useCallback(
    (keys, delay = 0) => {
      const requested = variations.filter((variation) =>
        keys.includes(variation.key),
      );
      const runnable = requested.filter(hasSavedVariation);
      requested
        .filter((variation) => !hasSavedVariation(variation))
        .forEach((variation) => unavailable(variation.label ?? variation.key));

      const byKey = new Map(runnable.map((variation) => [variation.key, variation]));
      const depth = (key, ancestors = new Set()) => {
        if (ancestors.has(key)) {
          return 0;
        }
        const next = new Set(ancestors).add(key);
        const references = getVariationReferenceKeys(byKey.get(key)).filter(
          (reference) => byKey.has(reference),
        );
        return references.length
          ? 1 + Math.max(...references.map((reference) => depth(reference, next)))
          : 0;
      };

      for (const variation of runnable) {
        const key = variation.key;
        const start = delay + depth(key) * DEMO_DELAY;
        const phase = (stage, label) =>
          setProgressByKey((previous) => ({
            ...previous,
            [key]: { stage, label },
          }));
        schedule(() => {
          setRevealedAssets((previous) => {
            const next = new Set(previous);
            next.delete(key);
            return next;
          });
          phase(VARIATION_PROGRESS_STAGE.WAITING, "Waiting");
        }, delay);
        schedule(
          () => phase(VARIATION_PROGRESS_STAGE.GENERATING, "Generating"),
          start + 1000,
        );
        schedule(
          () => phase(VARIATION_PROGRESS_STAGE.SAVING, "Saving"),
          start + 4000,
        );
        schedule(() => {
          setRevealedAssets((previous) => new Set(previous).add(key));
          setProgressByKey((previous) => {
            const next = { ...previous };
            delete next[key];
            return next;
          });
        }, start + DEMO_DELAY);
      }
    },
    [variations, hasSavedVariation, schedule, unavailable],
  );

  useEffect(() => {
    const delay = hasLoader ? DEMO_DELAY : 0;
    if (hasLoader) {
      schedule(() => setFinishedVisit(navigation.visit), delay);
    }
    if (isImagesStep(step) || isCampaignVisualsStep(step)) {
      simulateVariations(
        variations
          .filter(
            (variation) =>
              !revealedAssets.has(variation.key) && hasSavedVariation(variation),
          )
          .map((variation) => variation.key),
        delay,
      );
    }
    return cancelTimers;
    // Reveals survive a visit; only navigation starts a new simulation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigation.visit, step, hasLoader, schedule, cancelTimers]);

  const goTo = useCallback(
    (index) => {
      if (index < 0 || index >= steps.length || index === navigation.index) {
        return;
      }
      cancelTimers();
      setProgressByKey({});
      setUploadingKeys(new Set());
      setFurthestStep((previous) => Math.max(previous, index));
      setNavigation((previous) => ({
        index,
        visit: previous.visit + 1,
      }));
    },
    [steps.length, navigation.index, cancelTimers],
  );

  const chooseValue = useCallback(
    (key) => {
      if (!hasValue(values[key])) {
        unavailable(key);
        return;
      }
      setRevealedValues((previous) => new Set(previous).add(key));
    },
    [values, unavailable],
  );

  const chooseAsset = useCallback(
    (name) => {
      const asset = savedAsset(name);
      if (!asset || !(asset.filePath || asset.src || asset.color)) {
        unavailable(name);
        return;
      }
      setRevealedAssets((previous) => {
        const next = new Set(previous);
        next.delete(name);
        return next;
      });
      setUploadingKeys((previous) => new Set(previous).add(name));
      schedule(() => {
        setUploadingKeys((previous) => {
          const next = new Set(previous);
          next.delete(name);
          return next;
        });
        setRevealedAssets((previous) => new Set(previous).add(name));
      }, DEMO_DELAY);
    },
    [savedAsset, unavailable, schedule],
  );

  const managedNames = useMemo(
    () =>
      new Set([
        ...(template.assets ?? [])
          .filter((asset) => ["image", "color"].includes(asset.type))
          .map((asset) => asset.name),
        ...(template.imageVariations ?? []).map((variation) => variation.key),
      ]),
    [template],
  );
  const visibleProject = {
    ...saved,
    currentStep: step?.id,
    assets: [
      ...(saved.assets ?? []).filter(
        (asset) => !managedNames.has(asset.name) || revealedAssets.has(asset.name),
      ),
      ...[...uploadingKeys].map((name) => ({
        name,
        type: savedAsset(name)?.type,
        isLoading: true,
      })),
    ],
  };

  return {
    saved,
    steps,
    step,
    index: navigation.index,
    visit: navigation.visit,
    furthestStep,
    isStepPending,
    enterAction,
    project: visibleProject,
    values,
    value: (key) => (revealedValues.has(key) ? values[key] : null),
    chooseValue,
    chooseAsset,
    revealedAssets,
    uploadingKeys,
    progressByKey,
    variations,
    savedAsset,
    hasSavedVariation,
    simulateVariations,
    goTo,
    replayStep: () => {
      cancelTimers();
      setProgressByKey({});
      setUploadingKeys(new Set());
      setNavigation((previous) => ({
        ...previous,
        visit: previous.visit + 1,
      }));
    },
  };
};
